import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ChatService } from '../../../../../services/chat.service';
import {
  ISettingsItems,
  ISubSetting,
  KEYBOARD,
  SETTINGS_KEYS,
  SettingsForm,
  SUB_SETTINGS,
} from './chat-settings.interface';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BaseService } from '../../../../../services/base.service';
import { SettingsService } from '../../../../../services/settings.service';
import { finalize, Subject, switchMap, takeUntil } from 'rxjs';
import { MessagesTemplatesService } from '../../../../../services/messages-templates.service';
import { entityIcons, activeTranslations } from '../../../../../enum/constants';
import { DialogService } from '../../../../../services/dialog.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { ChatTag, TagService } from '../../../../../services/tag.service';

@Component({
  selector: 'app-chat-settings',
  templateUrl: './chat-settings.component.html',
  styleUrl: './chat-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatSettingsComponent implements OnInit, OnDestroy {
  @ViewChild('replyInput') replyInput!: ElementRef;
  @ViewChild('tagNameInput') tagNameInput?: ElementRef<HTMLElement>;
  @Output() onOpenChats = new EventEmitter();
  private readonly destroy$: Subject<null> = new Subject<null>();
  private readonly subSettings: ISubSetting[] = [
    { key: SUB_SETTINGS.GENERAL_SETTINGS, title: 'chat_settings.general_settings' },
    { key: SUB_SETTINGS.BLOCKED_USERS, title: 'chat_settings.blocked_users' },
    { key: SUB_SETTINGS.FAST_REPLY, title: 'chat_settings.fast_replies' },
    { key: SUB_SETTINGS.CHAT_TAGS, title: 'chat_settings.chat_tags' },
  ];
  protected isUpdate!: any | null;
  protected selectedSubSetting!: ISubSetting | null;
  protected textAreaControl!: FormControl;
  protected settingsItems!: ISettingsItems[];
  protected replies!: any[];
  protected settingsForm!: FormGroup<SettingsForm>;
  protected readonly colors: string[] = [
    '#ea5728',
    '#fa8231',
    '#4b7bec',
    '#483CE4',
    '#fc5c65',
    '#eb3b5a',
    '#fed330',
    '#f7b731',
    '#26de81',
    '#20bf6b',
    '#2bcbba',
    '#0fb9b1',
    '#45aaf2',
    '#2d98da',
    '#a55eea',
    '#8854d0',
    '#000000',
    '#d1d8e0',
  ];
  protected tags: ChatTag[] = [];
  protected canManageTags: boolean = false;
  protected editingTagId: string | null = null;
  protected isSavingTag: boolean = false;
  protected tagForm = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(64)] }),
    color: new FormControl(this.colors[0], { nonNullable: true, validators: [Validators.required] }),
  });

  constructor(
    protected readonly baseService: BaseService,
    protected readonly chatService: ChatService,
    protected readonly settingsService: SettingsService,
    protected readonly messagesTemplatesService: MessagesTemplatesService,
    private readonly dialogService: DialogService,
    private readonly notificationService: NotificationService,
    private readonly tagService: TagService,
  ) {}

  public ngOnInit(): void {
    this.initSettings();
    this.initSettingsForm();
    this.initReplies();
    this.initForm();
    this.loadTags();
    this.loadTagPermissions();
  }

  public ngOnDestroy(): void {
    this.initSettingsForm();
    this.chatService.blockedUsers = [];
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  protected initForm() {
    this.textAreaControl = new FormControl(null, Validators.required);
  }

  private initSettings(): void {
    this.settingsItems = [];

    this.settingsItems.push({
      key: SUB_SETTINGS.GENERAL_SETTINGS,
      title: 'chat_settings.general_settings',
      icon: 'settings',
      action: (item) => this.openSettings(item.key),
    });

    this.settingsItems.push({
      key: SUB_SETTINGS.BLOCKED_USERS,
      title: 'chat_settings.blocked_users',
      icon: 'block',
      action: (item) => this.openSettings(item.key),
    });

    this.settingsItems.push({
      key: SUB_SETTINGS.FAST_REPLY,
      title: 'chat_settings.fast_replies',
      icon: 'reply',
      action: (item) => this.openSettings(item.key),
    });

    this.settingsItems.push({
      key: SUB_SETTINGS.CHAT_TAGS,
      title: 'chat_settings.chat_tags',
      icon: 'sell',
      action: (item) => this.openSettings(item.key),
    });
  }

  private initSettingsForm() {
    this.settingsForm = new FormGroup({
      keyboard: new FormControl(this.baseService.currentUserInfo.settings.hot_key_send_chat),
      is_message_read: new FormControl(this.baseService.currentUserInfo.settings.is_message_read),
      is_lazy_loading: new FormControl(this.baseService.currentUserInfo.settings.is_lazy_loading),
      is_chat_unpin: new FormControl<boolean>(this.baseService.currentUserInfo.settings.is_chat_unpin),
    });
  }

  private openSettings(name: SUB_SETTINGS): void {
    const item = this.subSettings.find((item) => item.key === name);
    if (item) {
      if (item.key === SUB_SETTINGS.BLOCKED_USERS) this.chatService.getUsersBlocked();
      this.selectedSubSetting = item;
    }
  }

  protected closeLeftMenu(): void {
    if (this.selectedSubSetting) {
      this.selectedSubSetting = null;
      return;
    }
    this.chatService.closeLeftMenu();
  }

  protected changeSettings(key: string, value: boolean) {
    this.settingsService
      .updateSettings({ [key]: value })
      .pipe(switchMap(() => this.baseService.getProfile()))
      .subscribe();
  }

  protected isMacOS(): boolean {
    return /Macintosh|MacIntel|MacPPC|Mac68K/.test(window.navigator.userAgent);
  }

  protected unblockUser(id: string, type: string): void {
    this.chatService.unblockUser(id, type);
  }

  protected saveTag(): void {
    if (!this.canManageTags || this.tagForm.invalid || this.isSavingTag) return;
    const payload = { ...this.tagForm.getRawValue(), type: 'chat' as const };
    payload.name = payload.name.trim();

    if (this.hasDuplicateTagName(payload.name)) {
      this.setDuplicateTagNameError();
      return;
    }

    const request = this.editingTagId
      ? this.tagService.updateTag(this.editingTagId, payload)
      : this.tagService.createTag(payload);

    this.isSavingTag = true;
    request
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSavingTag = false)),
      )
      .subscribe({
        next: () => {
          this.resetTagForm();
          this.loadTags();
        },
        error: (response: any) => {
          if (response?.error?.errors?.some((error: any) => error.code === 'DIRECTORY_ALREADY_EXISTS')) {
            this.setDuplicateTagNameError();
          }
        },
      });
  }

  protected submitTagForm(event: Event): void {
    event.preventDefault();
    this.saveTag();
  }

  private hasDuplicateTagName(name: string): boolean {
    return this.tags.some((tag) => tag.id !== this.editingTagId && tag.name === name);
  }

  private setDuplicateTagNameError(): void {
    this.tagForm.controls.name.setErrors({ duplicateTag: true });
    this.tagForm.controls.name.markAsTouched();
  }

  protected editTag(tag: ChatTag): void {
    if (!this.canManageTags) return;
    this.editingTagId = tag.id;
    this.tagForm.setValue({ name: tag.name, color: tag.color });

    requestAnimationFrame(() => {
      this.tagNameInput?.nativeElement.querySelector('input')?.focus({ preventScroll: true });
    });
  }

  protected deleteTag(tag: ChatTag): void {
    if (!this.canManageTags) return;
    this.tagService
      .deleteTag(tag.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: () => this.loadTags() });
  }

  protected resetTagForm(): void {
    this.editingTagId = null;
    this.tagForm.reset({ name: '', color: this.colors[0] });
    this.tagForm.markAsPristine();
    this.tagForm.markAsUntouched();
  }

  private loadTags(): void {
    this.tagService
      .getTags()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (tags) => (this.tags = tags), error: () => (this.tags = []) });
  }

  private loadTagPermissions(): void {
    this.tagService
      .getPermissions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (permissions) => (this.canManageTags = permissions.can_manage) });
  }

  protected initReplies() {
    this.messagesTemplatesService.get().subscribe({
      next: (res) => {
        this.replies = res;
      },
    });
  }

  editTemplate(template: any) {
    this.isUpdate = template;
    this.textAreaControl.patchValue(template.text);
    this.replyInput.nativeElement.focus();
  }

  delete(template: any) {
    const dialogRef = this.dialogService.openConfirm({
      // text: `"delete_reply_text" +':\n${template.text}?'`,
      text: 'dialogs.chats-settings',
    });
    dialogRef.subscribe({
      next: (result) => {
        if (result) {
          this.messagesTemplatesService.delete(template.id).subscribe({
            next: () => {
              this.initReplies();
            },
          });
        }
      },
      error: () => {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error.error_delete,
        );
      },
    });
  }

  cancel() {
    this.textAreaControl.patchValue('');
    this.isUpdate = null;
  }

  update() {
    const text = this.textAreaControl.getRawValue();
    const parseText = (text || '').trim();
    if (this.textAreaControl.valid && parseText && this.isUpdate) {
      this.messagesTemplatesService.update(this.isUpdate.id, { text: parseText }).subscribe({
        next: () => {
          this.isUpdate = null;
          this.textAreaControl.reset();
          this.initReplies();
        },
        error: () => {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.error_updating,
          );
        },
      });
    }
  }

  save() {
    const text = this.textAreaControl.getRawValue();
    const parseText = (text || '').trim();

    if (this.textAreaControl.valid && parseText) {
      this.messagesTemplatesService.save({ text: parseText }).subscribe({
        next: () => {
          this.textAreaControl.reset();
          this.initReplies();
        },
        error: () => {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.error_saving,
          );
        },
      });
    }
  }

  dropReply(event: CdkDragDrop<any[]>) {
    if (!this.replies?.length) return;
    moveItemInArray(this.replies, event.previousIndex, event.currentIndex);
    this.saveRepliesOrder();
  }

  saveRepliesOrder() {
    const ids = this.replies.map((reply, index) => {
      return { id: reply.id, position: index + 1 };
    });
    this.messagesTemplatesService.reorder(ids).subscribe({
      error: () => {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error.error_updating,
        );
        this.initReplies();
      },
    });
  }

  protected readonly Object = Object;
  protected readonly SUB_SETTINGS = SUB_SETTINGS;
  protected readonly KEYBOARD = KEYBOARD;
  protected readonly SETTINGS_KEYS = SETTINGS_KEYS;
  protected readonly entityIcons = entityIcons;
}
